import I18n from 'I18n';
import getLangLocale from 'I18n/utils/getLangLocale';
import { formatChatspotApiMessage } from 'chatspot-core/messages/api/formatChatspotApiMessage';
import { useCopilotAppContext } from 'copilot-toolkit/hooks/useCopilotAppContext';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useCopilotV2PublishAblyAction } from '../../ably/hooks/useCopilotV2PublishAblyAction';
import { selectAssistant } from '../../assistants/selectors/assistants';
import { Paths } from '../../components/Routes';
import { useIsUngatedForCopilotV2 } from '../../gating/hooks/useIsUngatedForCopilotV2';
import { useHostAppContext } from '../../hostAppContext/hooks/useAppContext';
import { getPageTitle, getPageUrl } from '../../utils/pageContextUtil';
import { usageTracker } from '../../utils/usageTracker';
import { streamingMessageActionSent } from '../actions/streamingMessageActionSent';
import { selectActiveThreadId } from '../selectors/chatSelectors';
import { chatEventStreamClosed, chatEventStreamError, chatEventStreamUpdate } from '../slices/chat';
import { getMessageTracking } from '../utils/chatRequestUtil';
import { isCollaborateMode } from '../utils/collabModeUtil';
import { useIsSendMessageDisabled } from './useIsSendMessageDisabled';
import { useShouldSendCsatSurveyLegacy } from './useShouldSendCsatSurveyLegacy';
import { getBreezeAssistantChirpToolsFromLocalStorage } from '../../ably/AblyClient';
import { getBreezeAssistantReasoningModelFromLocalStorage } from '../../ably/AblyClient';
import { useCollaborationParentState } from '../../hooks/useCollaborationParentState';
import { INTERACTIVE_MODE_PLUGIN } from '../../types/PluginTypes';
const waitForI18n = () => {
  return new Promise(resolve => {
    if (typeof I18n.text === 'function') {
      resolve();
    } else {
      const checkInterval = setInterval(() => {
        if (typeof I18n.text === 'function') {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Add timeout to avoid infinite waiting
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 5000);
    }
  });
};
export const useSendMessageAction = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    sourceHostApp,
    sourceHostAppPathname
  } = useHostAppContext();
  const isSendMessageDisabled = useIsSendMessageDisabled();
  const currentActiveThreadId = useSelector(selectActiveThreadId);
  const shouldSendCsatSurveyLegacy = useShouldSendCsatSurveyLegacy();
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const publishAblyAction = useCopilotV2PublishAblyAction();
  const selectedAssistant = useSelector(selectAssistant);
  const appContext = useCopilotAppContext();
  const chirpTools = getBreezeAssistantChirpToolsFromLocalStorage();
  const reasoningModel = getBreezeAssistantReasoningModelFromLocalStorage();
  const {
    getParentAppStateByPlugin
  } = useCollaborationParentState();
  const {
    customFEActions
  } = getParentAppStateByPlugin(INTERACTIVE_MODE_PLUGIN) || {};
  return useCallback((messageAction, {
    source,
    type,
    template,
    pageContext,
    isSavedPrompt
  }, threadId) => {
    if (isSendMessageDisabled) {
      return;
    }
    history.push(Paths.CHAT);
    usageTracker.track('chatspotCoreInteraction', {
      action: 'message_action_sent',
      source,
      subaction: type
    });
    const onProgress = (event, data) => {
      if (event === 'message') {
        dispatch(chatEventStreamUpdate(formatChatspotApiMessage(data, source)));
      }

      // We currently don't handle progress events for message actions
    };
    const onClose = (statusCode, event, data) => {
      var _data$query;
      usageTracker.track('chatspotCoreInteraction', {
        action: 'prompt_submitted',
        intent: data === null || data === void 0 || (_data$query = data.query) === null || _data$query === void 0 ? void 0 : _data$query.intent,
        successful: !(data !== null && data !== void 0 && data.error),
        error: data === null || data === void 0 ? void 0 : data.error,
        statusCode,
        source,
        type
      });
      dispatch(chatEventStreamClosed());
      shouldSendCsatSurveyLegacy();
    };
    const onError = (e, shouldStopStream) => {
      if (shouldStopStream) {
        dispatch(chatEventStreamError());
      }
      usageTracker.track('chatspotCoreInteraction', {
        action: 'streaming_error',
        error: e.message,
        source,
        type
      });
      Raven.captureException(e);
    };
    const additionalToolSets = [...new Set([...(chirpTools || []), ...((customFEActions === null || customFEActions === void 0 ? void 0 : customFEActions.additionalToolSets) || [])])];
    waitForI18n().then(() => {
      if (isUngatedForCopilotV2) {
        // TODO: Consolidate more of this in a util we can use for both sendMessageAction and sendChatMessage
        publishAblyAction(Object.assign({
          isCollaborateMode: isCollaborateMode(source, type !== null && type !== void 0 ? type : ''),
          context: {
            // TODO: @mlegocki: handle file-upload data
            files: [],
            clientType: 'DESKTOP',
            crmObjectContext: appContext.crmObjectContext,
            locale: getLangLocale(),
            pageContext: {
              title: getPageTitle(),
              url: getPageUrl(),
              sourceHostApp
            },
            collabMode: false,
            // Always send false for actions, otherwise we confuse the LLM. The backend should use the toolInputs we pass instead.
            chirpToolFqcns: [],
            additionalToolSets: additionalToolSets || [],
            defaultToolSet: (customFEActions === null || customFEActions === void 0 ? void 0 : customFEActions.defaultToolSet) || undefined,
            reasoningModel: reasoningModel || undefined
          }
        }, getMessageTracking({
          template,
          isSavedPrompt
        }), {
          customCopilotId: selectedAssistant === null || selectedAssistant === void 0 ? void 0 : selectedAssistant.id,
          messageAction,
          type: 'ACTION',
          sourceHostAppPathname,
          // TODO: BE is not currently using this, but we had it for v1 - determine if it is needed for tracking in v2
          template
        }));
      } else {
        dispatch(streamingMessageActionSent({
          locale: getLangLocale(),
          onError,
          onProgress,
          onClose,
          messageAction,
          source,
          sourceHostApp,
          sourceHostAppPathname,
          threadId: threadId !== null && threadId !== void 0 ? threadId : currentActiveThreadId,
          template,
          pageContext,
          isCollaborateMode: source === 'PLUGIN'
        })).catch(error => {
          Raven.captureException(error, {
            extra: {
              component: 'useSendMessageAction',
              action: 'streamingMessageActionSent',
              context: 'Message action dispatch failed',
              source,
              hasMessageAction: !!messageAction
            }
          });
        });
      }
    }).catch(error => {
      console.error('Failed to wait for I18n');
      Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: {
          component: 'useSendMessageAction',
          action: 'waitForI18n',
          context: 'I18n loading timeout or failure',
          source,
          hasMessageAction: !!messageAction
        }
      });
    });
  }, [isSendMessageDisabled, history, chirpTools, customFEActions, dispatch, shouldSendCsatSurveyLegacy, isUngatedForCopilotV2, publishAblyAction, appContext, sourceHostApp, reasoningModel, selectedAssistant, sourceHostAppPathname, currentActiveThreadId]);
};